import { type IErrorApiResponse, type ISuccessApiResponse } from '@wyz/types';
import {
  OverridableQuery,
  UseQueryOptions,
  restrictApi,
  useQuery,
} from '@wyz/utils';
import { CommonQueryKeys } from '~/common/api';
import { useApi, useConfig } from '~/common/context';
import {
  IContainerSupplierGetByIdResponse,
  IContainerSupplierHqResponse,
} from '~/common/types';

type Options = {
  listHQs?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<IContainerSupplierHqResponse[]>,
      IErrorApiResponse
    >
  >;
  getById?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<IContainerSupplierGetByIdResponse>,
      IErrorApiResponse
    >
  > & {
    args: {
      id: number;
    };
  };
};

export const useManageContainerSuppliers = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { callCoreApi } = useApi();
  const { tenantCode } = useConfig();
  const getSupplierHQsQuery = useQuery({
    queryKey: CommonQueryKeys.containerSuppliers.hqs(),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<IContainerSupplierHqResponse[]>>(
        `/tenants/api/wtb/tenants/${tenantCode}/container-suppliers/hqs`,
      ),
    refetchOnWindowFocus: false,
    ...options.listHQs,
  });

  const getSupplierByIdQuery = useQuery({
    queryKey: CommonQueryKeys.containerSuppliers.byId(
      options.getById?.args.id ?? 0,
    ),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<IContainerSupplierGetByIdResponse>>(
        `/tenants/api/wtb/tenants/${tenantCode}/container-suppliers/${options.getById?.args.id}`,
      ),
    refetchOnWindowFocus: false,
    ...options.getById,
    enabled: Boolean(options.getById?.args.id && options.getById.enabled),
  });

  return restrictApi(options, {
    listHQs: getSupplierHQsQuery,
    getById: getSupplierByIdQuery,
  });
};
